import { useTransaction } from "hooks/useTransactions";
import { isEmpty } from "lodash";
import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import React, { useState } from "react";
import { bool } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { actions as transferActions } from "reducers/transfers";
import * as i18n from "util/i18n";
import gaEventTracker, { events } from "util/gaEventTracker";
import { transactionStatus } from "pages/transactions/constants";
import MepTransactionBody from "./components/TransactionBody";

const confirmRoute = `/mep/confirm`;

const MepTransactionPage = ({ isDesktop }) => {
    const [showMoreData, setShowMoreData] = useState(!isDesktop);
    const formatTransactionTransfer = (data, idTransaction) => ({ ...data, idTransaction });

    const {
        isLoading,
        transaction,
        dispatch,
        handleSignTransaction,
        showSignButton,
        push,
        id,
        status,
        routerState,
    } = useTransaction(
        confirmRoute,
        transferActions.setOtherTransferData,
        transferActions.cleanTransferTicket,
        formatTransactionTransfer,
    );

    const { isFinished, isFlowComplete, isPending, isScheduled, isBackoffice } = status;

    const isAwaiting = transaction?.data?.awaitingApproval || false;
    const mepTransactionStatus = isAwaiting && isPending ? transactionStatus.AWAITING : transaction.idTransactionStatus;

    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        "mep.title",
        id,
        "OtherTransferTransaction",
    );

    const fromContactPage = !isEmpty(routerState?.beneficiary);
    // eslint-disable-next-line no-nested-ternary
    const backLinkTo = fromContactPage ? "/contactDestination" : isFlowComplete ? "/transfers" : "";

    const getMdOffset = () => {
        if (isFinished || isAwaiting || showSignButton) {
            return 2;
        }
        return 4;
    };

    const handleSign = () => {
        gaEventTracker.triggerEvent(events.trans_pagos_firmar());
        return handleSignTransaction();
    };

    const handleDownload = async () => {
        await setShowMoreData(true);
        gaEventTracker.triggerEvent(events.trans_pagos_descargar());
        return handlePrint();
    };

    return (
        <>
            {isFinished && <Notification scopeToShow="OtherTransferTransaction" />}
            {!isBackoffice && <ResponsiveHeader ticket metaTitle="mep.title" backLinkTo={backLinkTo} />}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader
                                title="mep.title"
                                subTitle={`ticket.status.${mepTransactionStatus}`}
                                status={mepTransactionStatus}
                            />

                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    <MepTransactionBody
                                        setShowMoreData={setShowMoreData}
                                        showMoreData={showMoreData}
                                        isDesktop={isDesktop}
                                    />

                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions no-print">
                            <Col xs={12} md={4} mdOffset={getMdOffset()}>
                                <Button
                                    onClick={() => dispatch(push("/desktop"))}
                                    label="forms.transaction.ticket.actions.backToHome"
                                    className={`btn${
                                        isFinished || isAwaiting || showSignButton ? " btn-outline" : " btn-primary"
                                    }`}
                                />
                            </Col>
                            <Col xs={12} md={4}>
                                {isFinished || isAwaiting ? (
                                    <Button
                                        className="btn btn-primary"
                                        image="images/figma/icon/download.svg"
                                        label="global.download"
                                        onClick={handleDownload}
                                    />
                                ) : (
                                    <>
                                        {showSignButton && (
                                            <Button
                                                onClick={handleSign}
                                                label="forms.signTransaction.link"
                                                className="btn btn-primary"
                                            />
                                        )}
                                    </>
                                )}
                            </Col>
                            {isFinished && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </>
    );
};

MepTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default MepTransactionPage;
